import React, { useMemo } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert } from 'reactstrap';
import Breadcrumbs from 'components/Common/Breadcrumb';
import TableContainer from 'components/Common/TableContainer';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { useListQuery } from 'helpers/http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'helpers/http-client/httpConstants';

interface SalesInvoice {
  id: string;
  invoiceNumber: string;
  saleDate: string;
  customerName?: string;
  totalAmount: number;
  totalDiscount?: number;
}

const SalesInvoiceList: React.FC = () => {
  const navigate = useNavigate();

  const {
    data: sales,
    isLoading,
    isError,
  } = useListQuery<SalesInvoice>(API_ENDPOINTS.SALES_INVOICE);

  const transformedData: SalesInvoice[] = useMemo(() => {
    if (!sales) return [];
    return sales.map((invoice) => ({
      id: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      saleDate: invoice.saleDate,
      customerName: invoice.customerName || '',
      totalAmount: invoice.totalAmount,
      totalDiscount: invoice.totalDiscount,
    }));
  }, [sales]);

  const columns: ColumnDef<SalesInvoice>[] = useMemo(
    () => [
      {
        header: 'Invoice #',
        accessorKey: 'invoiceNumber',
        enableSorting: true,
        cell: (cellProps) => (
          <Link to={`/sales/invoices/${cellProps.row.original.id}`} className="text-dark fw-bold">
            {cellProps.row.original.invoiceNumber}
          </Link>
        ),
      },
      {
        accessorKey: 'saleDate',
        header: 'Sale Date',
        enableSorting: true,
        sortDescFirst: true,
        cell: (cellProps) => moment(cellProps.row.original.saleDate).format('DD MMM, YYYY'),
      },
      {
        header: 'Customer',
        accessorKey: 'customerName',
        cell: (cellProps) => cellProps.row.original.customerName || 'Walk-in',
      },
      {
        header: 'Total Amount',
        accessorKey: 'totalAmount',
        cell: (cellProps) => `Rs. ${cellProps.row.original.totalAmount.toFixed(2)}`,
      },
    ],
    [],
  );

  const handleAddInvoice = () => {
    navigate('/sales/invoices/add');
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Sales" breadcrumbItem="Sales Invoices" />
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-3">Loading sales invoices...</p>
                  </div>
                ) : isError ? (
                  <Alert color="danger">
                    Failed to load sales invoices. Please try again later.
                  </Alert>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={transformedData}
                    isGlobalFilter={true}
                    isAddButton={false}
                    handleUserClick={handleAddInvoice}
                    isPagination={true}
                    isCustomPageSize={true}
                    buttonClass="btn btn-primary btn-rounded"
                    buttonName="Add Sales Invoice"
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-borderless"
                    theadClass="table-light"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    paginationClass="pagination"
                    SearchPlaceholder="Search sales invoices..."
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SalesInvoiceList;
